import {
    OpenSalesOrderAction,
    OpenSalesOrderActionData,
    OpenSalesOrderActionId
} from "../shared/bridge/actions/open-sales-order.action";
import {
    OpenCustomerByCustomerIdAction,
    OpenCustomerByCustomerIdActionData,
    OpenCustomerByCustomerIdActionId
} from "./../shared/bridge/actions/open-customer-by-customerId.action";
import {
    OpenInvoiceByInvoiceUqAction,
    OpenInvoiceByInvoiceUqActionData,
    OpenInvoiceByInvoiceUqActionId
} from "./../shared/bridge/actions/open-invoice-by-invoiceUq.action";

import { ChangeDetectionStrategy, Component, inject, OnDestroy } from "@angular/core";
import {
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    UntypedFormControl,
    Validators
} from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { ApplicationContext } from "@vega/application-context";
import { BridgeService } from "@vega/bridge";
import { ITranslateService, TRANSLATE_SERVICE, TranslatePipe } from "@vega/localization";
import { BehaviorSubject, Subscription, catchError, map, switchMap, tap, withLatestFrom } from "rxjs";
import { TransactionViewModel, flattenTransactions } from "../shared/models/transaction-view.model";
import { TransactionService } from "./transaction-service";
import { CommonModule } from "@angular/common";
import { GRID_DIRECTIVES, GridComponent, IgxGridColumnDataType, IIgxColumn } from "@vega/igx-grid";
import { IconComponent } from "@vega/ui-visualization/icon";
import { DateFormatterPipe } from "@vega/ui-common/date-formatter";
import { ControlGroupComponent } from "@vega/ui-toolbox/control-group";
import { PricePipe } from "../../shared/pipes/price.pipe";
import { CalendarPickerDirective } from "@vega/calendar";

@Component({
    selector: "vega-transactions-overview",
    templateUrl: "./transactions-overview.component.html",
    styleUrls: ["./transactions-overview.component.less"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [TransactionService],
    imports: [
        CommonModule,
        GridComponent,
        ...GRID_DIRECTIVES,
        IconComponent,
        DateFormatterPipe,
        TranslatePipe,
        ControlGroupComponent,
        PricePipe,
        FormsModule,
        ReactiveFormsModule,
        CalendarPickerDirective
    ]
})
export class TransactionsOverviewComponent implements OnDestroy {
    private _transactionService = inject(TransactionService);
    private _activatedRoute = inject(ActivatedRoute);
    private _applicationContext = inject(ApplicationContext);
    private _formBuilder = inject(FormBuilder);
    private _bridgeService = inject(BridgeService);
    private _translateService = inject(TRANSLATE_SERVICE);
    public overviewColumns = getGridColumns(this._translateService);
    public startDateCtrl: UntypedFormControl;
    public endDateCtrl: UntypedFormControl;
    public dateForm: FormGroup;
    public bridgeActive: boolean = this._bridgeService.isBridgeActive;
    private _activatedRouteSubscription: Subscription = new Subscription();
    private _transactionSubscription: Subscription = new Subscription();
    private now = new Date();
    private _startDate: Date = new Date(this.now.getFullYear(), this.now.getMonth(), this.now.getDate() - 30);
    private _endDate: Date = this.now;
    private _startEndDateBehaviorSubject = new BehaviorSubject({
        startDate: this._startDate,
        endDate: this._endDate,
        lastPayoutId: ""
    });
    private _isLoadingBehaviorSubject = new BehaviorSubject({ isLoading: true });
    private _hasMoreDataBehaviorSubject = new BehaviorSubject({ hasMore: false, lastPayoutId: "" });
    private _viewModelBehaviorSubject = new BehaviorSubject<TransactionViewModel[]>([]);
    public isLoading$ = this._isLoadingBehaviorSubject.pipe(map((x) => x.isLoading));
    public hasMore$ = this._hasMoreDataBehaviorSubject.pipe(map((x) => x.hasMore));
    public lastPayoutId$ = this._hasMoreDataBehaviorSubject.pipe(map((x) => x.lastPayoutId));
    public viewModel$ = this._startEndDateBehaviorSubject.pipe(
        tap(() => this._isLoadingBehaviorSubject.next({ isLoading: true })),
        switchMap(({ startDate, endDate, lastPayoutId }) =>
            this._transactionService.getTransactions(startDate, endDate, lastPayoutId).pipe(
                tap(() => this._isLoadingBehaviorSubject.next({ isLoading: false })),

                withLatestFrom(this._viewModelBehaviorSubject),
                map(([result, currentViewModel]) => {
                    const overviewList = flattenTransactions(result);

                    const newViewModel = lastPayoutId ? [...currentViewModel, ...overviewList] : overviewList;

                    this._hasMoreDataBehaviorSubject.next({
                        hasMore: result.hasMorePayout,
                        lastPayoutId: result.lastPayoutId
                    });

                    this._viewModelBehaviorSubject.next(newViewModel);
                    return newViewModel;
                }),
                catchError(() => {
                    this._isLoadingBehaviorSubject.next({ isLoading: false });
                    return [];
                })
            )
        )
    );
    constructor() {
        this.startDateCtrl = new UntypedFormControl(this._startDate, Validators.required);
        this.endDateCtrl = new UntypedFormControl(this._endDate, Validators.required);
        this.dateForm = this._formBuilder.group({
            startDateCtrl: this.startDateCtrl,
            endDateCtrl: this.endDateCtrl
        });
        const mandatorId = this._activatedRoute.snapshot.params["mandatorId"];
        if (mandatorId) {
            const mandatorFromRoute = parseInt(mandatorId, 10);
            if (!isNaN(mandatorFromRoute)) {
                if (this._applicationContext.mandatorId !== mandatorFromRoute) {
                    this._applicationContext.setMandatorId(mandatorFromRoute);
                    localStorage.setItem("mandatorId", this._activatedRoute.snapshot.params.mandatorId);
                }
            }
        }
    }

    public getTransactionTypeClass(transactionType: string): object {
        return {
            green: transactionType === "charge",
            red: transactionType === "refund",
            orange: transactionType === "network_cost",
            blue: transactionType === "stripe_fee"
        };
    }

    public getChargeAmountClass(amount: number): object {
        return {
            green: amount >= 0,
            red: amount < 0
        };
    }

    public onSearch(): void {
        if (this.dateForm.valid) {
            this._startEndDateBehaviorSubject.next({
                startDate: this.startDateCtrl.value,
                endDate: this.endDateCtrl.value,
                lastPayoutId: ""
            });
        }
    }

    public onLoadMore(lastPayoutId: string): void {
        if (this.dateForm.valid) {
            this._startEndDateBehaviorSubject.next({
                startDate: this.startDateCtrl.value,
                endDate: this.endDateCtrl.value,
                lastPayoutId
            });
        }
    }

    public onSelectInvoice(transaction: TransactionViewModel): void {
        this._bridgeService.callBridgeProvidedActionAsync<OpenInvoiceByInvoiceUqActionData, void>({
            id: OpenInvoiceByInvoiceUqActionId,
            argument: { invoiceUq: transaction.invoiceUq },
            alternativeAction: () => false
        } as unknown as OpenInvoiceByInvoiceUqAction);
    }
    public onSelectOrder(transaction: TransactionViewModel): void {
        this._bridgeService.callBridgeProvidedActionAsync<OpenSalesOrderActionData, void>({
            id: OpenSalesOrderActionId,
            argument: { salesOrderId: transaction.orderId, salesOrderItemId: 1, proceedingTypeId: 0 },
            alternativeAction: () => false
        } as unknown as OpenSalesOrderAction);
    }
    public onSelectCustomer(transaction: TransactionViewModel): void {
        this._bridgeService.callBridgeProvidedActionAsync<OpenCustomerByCustomerIdActionData, void>({
            id: OpenCustomerByCustomerIdActionId,
            argument: { customerId: transaction.customerId },
            alternativeAction: () => false
        } as unknown as OpenCustomerByCustomerIdAction);
    }

    public ngOnDestroy(): void {
        if (this._activatedRouteSubscription) {
            this._activatedRouteSubscription.unsubscribe();
        }
        if (this._transactionSubscription) {
            this._transactionSubscription.unsubscribe();
        }
    }
}

const getGridColumns = (translateService: ITranslateService): IIgxColumn<TransactionViewModel>[] => [
    {
        field: "payoutId",
        header: translateService.instant("OVERVIEW.PAYOUT_ID"),
        dataType: IgxGridColumnDataType.String,
        width: "200"
    },
    {
        field: "balanceTransactionId",
        header: translateService.instant("OVERVIEW.TRANSACTION_ID"),
        dataType: IgxGridColumnDataType.String,
        width: "200"
    },
    {
        field: "transactionType",
        header: translateService.instant("OVERVIEW.TRANSACTION_TYPE"),
        dataType: IgxGridColumnDataType.String,
        width: "200"
    },
    {
        field: "payoutAmount",
        header: translateService.instant("OVERVIEW.TOTAL_PAYOUT_AMOUNT"),
        dataType: IgxGridColumnDataType.Number,
        width: "200"
    },
    {
        field: "chargeId",
        header: translateService.instant("OVERVIEW.CHARGE_ID"),
        dataType: IgxGridColumnDataType.String,
        width: "200"
    },
    {
        field: "chargeAmount",
        header: translateService.instant("OVERVIEW.INVOICE_AMOUNT"),
        dataType: IgxGridColumnDataType.Number,
        width: "200"
    },
    {
        field: "customerId",
        header: translateService.instant("OVERVIEW.CUSTOMER_ID"),
        dataType: IgxGridColumnDataType.Number,
        width: "200"
    },
    {
        field: "invoiceId",
        header: translateService.instant("OVERVIEW.INVOICE_ID"),
        dataType: IgxGridColumnDataType.Number,
        width: "200"
    },
    {
        field: "orderId",
        header: translateService.instant("OVERVIEW.ORDER_ID"),
        dataType: IgxGridColumnDataType.Number,
        width: "200"
    },
    {
        field: "companyName",
        header: translateService.instant("OVERVIEW.COMPANY_NAME"),
        dataType: IgxGridColumnDataType.String,
        width: "200"
    },
    {
        field: "customerReference",
        header: translateService.instant("OVERVIEW.CUSTOMER_REFERENCE"),
        dataType: IgxGridColumnDataType.String,
        width: "200"
    },
    {
        field: "paid",
        header: translateService.instant("OVERVIEW.PAID"),
        dataType: IgxGridColumnDataType.Boolean,
        width: "200"
    },
    {
        field: "chargeCreateDate",
        header: translateService.instant("OVERVIEW.CREATE_DATE"),
        dataType: IgxGridColumnDataType.Date,
        width: "200"
    }
];
